import React from 'react';
import { displayManifestBrand, getLotProducts } from '../../utils/testRigHelpers/manifestTruth';

/**
 * The lot's ground truth, beside the answers graded against it. It is lot-level
 * truth — one line for every item of the lot — so the cell spans the lot's rows.
 *
 * A single-product lot prints the brand and product it was graded against. A
 * lot listing several products prints each product its manifest specifically
 * names, ✓ when an answer item names it. A run whose dataset ships no manifest
 * shows a dash; a lot nothing could be graded against says so.
 */
const NOT_GRADED =
  'Not graded: the manifest lists several products, or names nothing specific enough to grade.';

// The scorer keys a brand on its first word, so "Hampton Bay" shows as Hampton —
// and an answer of Hampton Bay, or Hampton, is right.
const BRAND_NOTE =
  'The brand word the answer is graded on. A multi-word brand shows its first word; any answer starting with it counts.';

const SingleProduct = ({ expectedName, expectedBrand }) => {
  const brand = displayManifestBrand(expectedName, expectedBrand);
  return (
    <div className="space-y-0.5">
      <div title={BRAND_NOTE}>
        <span className="font-semibold">Brand:</span>{' '}
        {brand || <span className="italic text-muted">none named</span>}
      </div>
      <div className="break-words">
        <span className="font-semibold">Product:</span> {expectedName}
      </div>
    </div>
  );
};

const ProductList = ({ products, found }) => (
  <div className="space-y-0.5">
    <div className="font-semibold">Products found: {found} / {products.length}</div>
    {products.map((product, index) => (
      <div
        key={`${product.name}-${index}`}
        className="break-words"
        title={product.found ? `Named by the answer as: ${product.matchedItem}` : 'No answer item names this product.'}
      >
        <span className={product.found ? 'text-positive' : 'text-danger'}>{product.found ? '✓' : '✗'}</span>{' '}
        {product.brand ? <span className="font-semibold">{product.brand} · </span> : null}
        {product.name}
        {product.brandCorrect === false ? <span className="text-danger"> (brand ✗)</span> : null}
      </div>
    ))}
  </div>
);

const ManifestTruthCell = ({ result, run, rowSpan }) => {
  const runWasGraded = Boolean(run && typeof run.identification === 'object' && run.identification);
  const expectedName = typeof result?.expected_name === 'string' ? result.expected_name : '';
  const expectedBrand = typeof result?.expected_brand === 'string' ? result.expected_brand : '';
  const lotProducts = getLotProducts(result);

  let body;
  if (!runWasGraded) {
    body = <span className="text-muted" title="This run's dataset ships no manifest lines.">-</span>;
  } else if (expectedName) {
    body = <SingleProduct expectedName={expectedName} expectedBrand={expectedBrand} />;
  } else if (lotProducts.judged && lotProducts.products.length) {
    body = <ProductList products={lotProducts.products} found={lotProducts.found} />;
  } else if (lotProducts.judged) {
    body = <span className="italic text-muted">Not graded: the manifest names no specific product.</span>;
  } else if (lotProducts.pending) {
    body = <span className="italic text-muted">Several products — being judged…</span>;
  } else {
    body = <span className="italic text-muted" title={NOT_GRADED}>Not graded</span>;
  }
  const wrongClaims = Number(result?.extras_wrong) || 0;
  return (
    <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={rowSpan}>
      {body}
      {wrongClaims > 0 ? (
        <div
          className="mt-0.5 text-danger"
          title="Products the answer names that this lot's manifest does not contain — each a wrong claim, counted against Product."
        >
          Named but not in this lot: {wrongClaims}
        </div>
      ) : null}
    </td>
  );
};

export default ManifestTruthCell;
